import React from 'react'
import { 
  Users, 
  PenTool, 
  BarChart3, 
  Search, 
  FileText, 
  Database,
  CheckSquare,
  Brain,
  Globe,
  Calculator
} from 'lucide-react'

interface NodeTypeConfig {
  type: string
  label: string
  description: string
  icon: React.ComponentType<any>
  color: string
  category: 'agents' | 'tools' | 'tasks'
}

const nodeTypes: NodeTypeConfig[] = [
  // Agents
  {
    type: 'agent',
    label: 'Research Agent',
    description: 'Gathers and analyzes information',
    icon: Users,
    color: 'agent',
    category: 'agents'
  },
  {
    type: 'agent',
    label: 'Writer Agent',
    description: 'Creates content and documentation',
    icon: PenTool,
    color: 'agent',
    category: 'agents'
  },
  {
    type: 'agent',
    label: 'Analyst Agent',
    description: 'Processes and evaluates data',
    icon: BarChart3,
    color: 'agent',
    category: 'agents'
  },
  {
    type: 'agent',
    label: 'Decision Agent',
    description: 'Makes strategic decisions',
    icon: Brain,
    color: 'agent',
    category: 'agents'
  },
  
  // Tools
  {
    type: 'tool',
    label: 'Web Search',
    description: 'Search the internet for information',
    icon: Search,
    color: 'tool',
    category: 'tools'
  },
  {
    type: 'tool',
    label: 'File Reader',
    description: 'Read and process documents',
    icon: FileText,
    color: 'tool',
    category: 'tools'
  },
  {
    type: 'tool',
    label: 'Database Query',
    description: 'Query databases for information',
    icon: Database,
    color: 'tool',
    category: 'tools'
  },
  {
    type: 'tool',
    label: 'API Call',
    description: 'Make HTTP requests to external APIs',
    icon: Globe,
    color: 'tool',
    category: 'tools'
  },
  {
    type: 'tool',
    label: 'Calculator',
    description: 'Perform mathematical calculations',
    icon: Calculator,
    color: 'tool',
    category: 'tools'
  },
  
  // Tasks
  {
    type: 'task',
    label: 'Analysis Task',
    description: 'Perform data analysis',
    icon: CheckSquare,
    color: 'task',
    category: 'tasks'
  },
  {
    type: 'task',
    label: 'Writing Task',
    description: 'Create written content',
    icon: PenTool,
    color: 'task',
    category: 'tasks'
  },
  {
    type: 'task',
    label: 'Research Task',
    description: 'Gather information on a topic',
    icon: Search,
    color: 'task',
    category: 'tasks'
  }
]

const NodePalette: React.FC = () => {
  const onDragStart = (event: React.DragEvent, nodeType: string, label: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType)
    event.dataTransfer.setData('application/reactflow/label', label)
    event.dataTransfer.effectAllowed = 'move'
  }

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, { bg: string; text: string; hover: string }> = {
      agent: { bg: 'bg-blue-100 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-400', hover: 'hover:bg-blue-50 dark:hover:bg-blue-900/30' },
      tool: { bg: 'bg-purple-100 dark:bg-purple-900/20', text: 'text-purple-600 dark:text-purple-400', hover: 'hover:bg-purple-50 dark:hover:bg-purple-900/30' },
      task: { bg: 'bg-green-100 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-400', hover: 'hover:bg-green-50 dark:hover:bg-green-900/30' },
      // Legacy colors for backward compatibility
      blue: { bg: 'bg-blue-100 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-400', hover: 'hover:bg-blue-50 dark:hover:bg-blue-900/30' },
      green: { bg: 'bg-green-100 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-400', hover: 'hover:bg-green-50 dark:hover:bg-green-900/30' },
      purple: { bg: 'bg-purple-100 dark:bg-purple-900/20', text: 'text-purple-600 dark:text-purple-400', hover: 'hover:bg-purple-50 dark:hover:bg-purple-900/30' },
    }
    return colorMap[color] || colorMap.agent
  }

  const groupedNodes = nodeTypes.reduce((acc, node) => {
    if (!acc[node.category]) {
      acc[node.category] = []
    }
    acc[node.category].push(node)
    return acc
  }, {} as Record<string, NodeTypeConfig[]>)

  const categoryConfig = {
    agents: { title: 'Agents', icon: Users },
    tools: { title: 'Tools', icon: Search },
    tasks: { title: 'Tasks', icon: CheckSquare }
  }

  return (
    <div className="w-80 sidebar border-r flex flex-col">
      <div className="p-6 border-b border-gray-100 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Components
        </h2>
        
        <div className="space-y-6">
          {Object.entries(groupedNodes).map(([category, nodes]) => {
            const config = categoryConfig[category as keyof typeof categoryConfig]
            const Icon = config.icon
            
            return (
              <div key={category} className="space-y-3">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                  <Icon className="w-4 h-4 mr-2" />
                  {config.title}
                </h3>
                
                <div className="space-y-2">
                  {nodes.map((node, index) => {
                    const NodeIcon = node.icon
                    const colors = getColorClasses(node.color)
                    
                    return (
                      <div
                        key={`${node.type}-${index}`}
                        className={`card p-3 cursor-grab active:cursor-grabbing transition-all duration-200 ${colors.hover} hover:shadow-md hover:-translate-y-0.5`}
                        draggable
                        onDragStart={(event) => onDragStart(event, node.type, node.label)}
                      >
                        <div className="flex items-center">
                          <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center mr-3`}>
                            <NodeIcon className={`${colors.text} w-5 h-5`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 dark:text-white text-sm truncate">
                              {node.label}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {node.description}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      
      {/* Quick Tips */}
      <div className="p-6 border-b border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Quick Tips
        </h3>
        <div className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-start">
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
            <span>Drag components onto the canvas to add them</span>
          </div>
          <div className="flex items-start">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
            <span>Connect nodes by dragging from connection points</span>
          </div>
          <div className="flex items-start">
            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
            <span>Click nodes to configure their settings</span>
          </div>
          <div className="flex items-start">
            <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
            <span>Use the Kickoff button to execute your workflow</span>
          </div>
        </div>
      </div>
      
      {/* Workflow Templates */}
      <div className="p-6 flex-1">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Templates
        </h3>
        <div className="space-y-2">
          {[
            { name: 'Market Research', description: 'Research → Analysis → Report' },
            { name: 'Content Creation', description: 'Research → Write → Edit' },
            { name: 'Data Processing', description: 'Collect → Clean → Analyze' },
          ].map((template, index) => (
            <button
              key={index}
              className="w-full text-left card p-3 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
            >
              <div className="font-medium text-gray-900 dark:text-white text-sm">
                {template.name}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {template.description}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default NodePalette
